// Sprint PKG-VIZ A1 (#792) — shared internal rendering shell for the 6
// named node components + the fallback. NOT part of the @blueprint/shared
// contract (S2) — purely an apps/web implementation detail to avoid
// repeating Handle/card boilerplate 7 times.
//
// Implementation note: the issue's prose describes each node as a single
// diamond/triangle/circle-shaped card. A whole multi-line text card
// clipped into a diamond or triangle clips away label/badge text at the
// corners, so this renders the shape as a small icon swatch (clip-path)
// next to a normal readable rectangular card body — same shape+icon+color
// language, legible content. A1's contract is the prop/type surface, not
// pixel-exact shape geometry (see impl-prompt §2/S2).

import { Handle, Position } from '@xyflow/react';
import type { LucideIcon } from 'lucide-react';

export type NodeShellShape = 'diamond' | 'triangle' | 'square' | 'circle' | 'dotted-card' | 'square-outline';

const SHAPE_CLIP_PATH: Partial<Record<NodeShellShape, string>> = {
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  triangle: 'polygon(50% 5%, 5% 95%, 95% 95%)',
  circle: 'circle(50% at 50% 50%)',
};

export interface NodeShellProps {
  shape: NodeShellShape;
  accentColor: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
  dashedBorder?: boolean;
  outlineOnly?: boolean;
  testId: string;
  /**
   * PKGV.D2 (#802) — forwarded from React Flow's own per-node
   * isConnectable (computed from PKGCanvas's nodesConnectable prop,
   * itself gated on interactionLevel==='editable'). Without this, the
   * two <Handle> elements below default to their own isConnectable=true
   * regardless of the canvas-level setting — a real gap found while
   * testing D2's onConnect wiring: React Flow passes isConnectable to
   * the registered node component (RiskNode/DecisionNode/etc.), but
   * nothing forwarded it down to NodeShell's actual <Handle> elements
   * before this. Defaults to true only because Handle's own upstream
   * default is true — callers that DO pass it (every node type, now)
   * always pass the real resolved value.
   */
  isConnectable?: boolean;
}

export function NodeShell({
  shape,
  accentColor,
  icon: Icon,
  label,
  badge,
  dashedBorder = false,
  outlineOnly = false,
  testId,
  isConnectable,
}: NodeShellProps) {
  const clipPath = SHAPE_CLIP_PATH[shape];

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        minWidth: 160,
        borderRadius: shape === 'square-outline' ? 6 : 8,
        background: outlineOnly ? 'transparent' : 'var(--surface)',
        border: `1px solid ${accentColor}`,
        borderStyle: dashedBorder ? 'dashed' : 'solid',
        fontFamily: 'var(--font-body, inherit)',
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      <div
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: accentColor,
          clipPath,
          borderRadius: clipPath ? undefined : 6,
        }}
      >
        <Icon size={14} color="#FFFFFF" strokeWidth={2} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
        {badge ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: accentColor,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={isConnectable} />
    </div>
  );
}
